import React, { useMemo } from "react";
import { Timestamp } from "firebase/firestore";
import CalendarIcon from "@heroicons/react/24/outline/CalendarIcon";
import CreditCardIcon from "@heroicons/react/24/outline/CreditCardIcon";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";
import ExclamationCircleIcon from "@heroicons/react/24/outline/ExclamationCircleIcon";
import CheckCircleSolidIcon from "@heroicons/react/24/solid/CheckCircleIcon";

// Local
import { ExistingMembership } from "../../models/membership";
import {
  ExistingMembershipTemplate,
  TaskGeneration,
} from "../../models/membership-template";
import { Task } from "../../models/task";
import { TaskStatus } from "../../models/task-status";
import { getDateFromStartDateAndDaysAfterRenewal } from "./MembershipTasksOverview";
import { getTaskStatusPillClasses } from "../../assets/js/tasks";
import { useNavigate } from "react-router-dom";
import { WORK_RECORD_AND_TASKS_URL } from "../../urls";

interface TimelineEvent {
  id: string;
  date: Date;
  type: "task" | "invoice" | "payment" | "start" | "end";
  status?: number;
  label: string;
  data?: any;
  row?: number; // Row for stacking events on same date
}

// Group to represent events clustered at the same position
interface EventCluster {
  position: number;
  events: TimelineEvent[];
}

interface Props {
  membership: ExistingMembership;
  membershipTemplate: ExistingMembershipTemplate;
  invoices?: any[];
  payments?: any[];
}

export default function MembershipTimelineHorizontal({
  membership,
  membershipTemplate,
  invoices = [],
  payments = [],
}: Props) {
  const navigate = useNavigate();

  // Function to handle navigating to the task/work record
  function handleGoToWorkRecordAndTaskPage(craftRecordPath: string) {
    const craftRecordID = craftRecordPath.split("/")[3];
    navigate(`${WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`);
  }

  // Generate event data only if we have both start and end dates
  const timelineData = useMemo(() => {
    if (!membership.membershipStartDate || !membership.membershipEndDate) {
      return null;
    }

    // Get start and end dates
    const startDate = membership.membershipStartDate.toDate();
    const endDate = membership.membershipEndDate.toDate();

    // Calculate total duration in months
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationDays = durationMs / (1000 * 60 * 60 * 24);
    const durationMonths = durationDays / 30.44; // Average days per month

    // Create events array
    const events: TimelineEvent[] = [
      {
        id: "start",
        date: startDate,
        type: "start",
        label: "Start",
      },
      {
        id: "end",
        date: endDate,
        type: "end",
        label: "End",
      },
    ];

    // Add tasks from membership
    if (membership.tasks) {
      Object.entries(membership.tasks).forEach(
        ([taskID, task]: [string, Task], index) => {
          if (task.timestampScheduled) {
            events.push({
              id: `task-${taskID}`,
              date: task.timestampScheduled.toDate(),
              type: "task",
              status: task.taskStatus,
              label: task.title || `Task ${index + 1}`,
              data: task,
            });
          }
        },
      );
    }

    // Add invoices
    if (membership.nextInvoiceDate) {
      events.push({
        id: "next-invoice",
        date: membership.nextInvoiceDate.toDate(),
        type: "invoice",
        label: "Next Invoice",
        data: { isNext: true },
      });
    }

    // Add payments
    if (membership.lastPaymentDate) {
      events.push({
        id: "last-payment",
        date: membership.lastPaymentDate.toDate(),
        type: "payment",
        label: "Last Payment",
        data: {
          amount: membership.lastPaymentAmount || 0,
          isLast: true,
        },
      });
    }

    // Sort events by date
    events.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Define padding percentages - add 5% padding on each side
    const leftPadding = 5;
    const rightPadding = 5;
    const contentWidth = 100 - leftPadding - rightPadding;

    // The total duration of the timeline
    const totalDuration = endDate.getTime() - startDate.getTime();

    // Calculate position percentage based on date with padding
    const calculatePosition = (date: Date) => {
      const timeFromStart = date.getTime() - startDate.getTime();
      const rawPercentage = (timeFromStart / totalDuration) * contentWidth;
      // Add left padding to position
      return Math.min(
        Math.max(leftPadding + rawPercentage, leftPadding),
        leftPadding + contentWidth,
      );
    };

    // Function to determine which events are close to each other and group them
    const createEventClusters = (events: TimelineEvent[]): EventCluster[] => {
      // First calculate positions for all events
      const eventsWithPositions = events.map((event) => ({
        ...event,
        position: calculatePosition(event.date),
      }));

      // Group events that are close to each other (less than 5% apart)
      const clusters: EventCluster[] = [];
      const threshold = 4; // percentage threshold for grouping

      eventsWithPositions.forEach((event) => {
        // Check if this event is close to any existing cluster
        const existingCluster = clusters.find(
          (cluster) => Math.abs(cluster.position - event.position) < threshold,
        );

        if (existingCluster) {
          // Add to existing cluster
          existingCluster.events.push(event);
          // Update cluster position to average of all events in it
          const totalPos = existingCluster.events.reduce(
            (sum, e) => sum + calculatePosition(e.date),
            0,
          );
          existingCluster.position = totalPos / existingCluster.events.length;
        } else {
          // Create new cluster
          clusters.push({
            position: event.position,
            events: [event],
          });
        }
      });

      return clusters;
    };

    // Create clusters of events that are close to each other
    const eventClusters = createEventClusters(events);

    // Month markers generation (one per month)
    const months = [];
    let currentMonth = new Date(startDate);
    while (currentMonth <= endDate) {
      months.push({
        date: new Date(currentMonth),
        position: calculatePosition(currentMonth),
      });
      currentMonth = new Date(
        currentMonth.setMonth(currentMonth.getMonth() + 1),
      );
    }

    return {
      startDate,
      endDate,
      events,
      eventClusters,
      months,
      calculatePosition,
    };
  }, [membership]);

  // Format date to MMM D, YY
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  // Get the icon for an event
  const getEventIcon = (event: TimelineEvent) => {
    switch (event.type) {
      case "start":
      case "end":
        return <CalendarIcon className="h-6 w-6 text-blue-600" />;
      case "task":
        if (event.status === TaskStatus.COMPLETE) {
          return <CheckCircleSolidIcon className="h-6 w-6 text-green-600" />;
        } else if (event.status === TaskStatus.IN_PROGRESS) {
          return <ClockIcon className="h-6 w-6 text-blue-600" />;
        } else {
          return <ExclamationCircleIcon className="h-6 w-6 text-gray-400" />;
        }
      case "invoice":
        return <DocumentTextIcon className="h-6 w-6 text-purple-600" />;
      case "payment":
        return <CreditCardIcon className="h-6 w-6 text-green-600" />;
      default:
        return <CalendarIcon className="h-6 w-6 text-gray-400" />;
    }
  };

  // Get color class for event
  const getEventColorClass = (event: TimelineEvent) => {
    switch (event.type) {
      case "start":
      case "end":
        return "bg-blue-100 border-blue-500";
      case "task":
        if (event.status === TaskStatus.COMPLETE) {
          return "bg-green-100 border-green-500";
        } else if (event.status === TaskStatus.IN_PROGRESS) {
          return "bg-blue-100 border-blue-500";
        } else {
          return "bg-gray-100 border-gray-400";
        }
      case "invoice":
        return "bg-purple-100 border-purple-500";
      case "payment":
        return "bg-green-100 border-green-500";
      default:
        return "bg-gray-100 border-gray-400";
    }
  };

  // Early return if we don't have timeline data
  if (!timelineData) {
    return (
      <div className="mt-4 text-center text-sm italic text-gray-500">
        {!membership.membershipStartDate
          ? "Timeline not available (no membership start date)"
          : "Timeline not available (no membership end date)"}
      </div>
    );
  }

  const { startDate, endDate, eventClusters, months } = timelineData;

  return (
    <div className="mb-8 mt-4 w-full">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-primary">
          Membership Timeline
        </h3>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center">
            <DocumentTextIcon className="mr-1 h-4 w-4 text-purple-600" />
            <span>Invoice</span>
          </div>
          <div className="flex items-center">
            <CreditCardIcon className="mr-1 h-4 w-4 text-green-600" />
            <span>Payment</span>
          </div>
          <div className="flex items-center">
            <ExclamationCircleIcon className="mr-1 h-4 w-4 text-gray-400" />
            <span>Planned Task</span>
          </div>
          <div className="flex items-center">
            <CheckCircleSolidIcon className="mr-1 h-4 w-4 text-green-600" />
            <span>Completed Task</span>
          </div>
        </div>
      </div>

      {/* Timeline container */}
      <div className="relative">
        {/* Timeline track with increased height to accommodate stacked events */}
        <div className="relative h-48 w-full overflow-hidden rounded-md border border-gray-300 bg-gray-50 p-4">
          {/* Month markers */}
          {months.map((month, index) => (
            <div
              key={`month-${index}`}
              className="absolute bottom-4 h-2 w-px bg-gray-400"
              style={{ left: `${month.position}%` }}
            >
              <div className="absolute -left-8 -top-6 w-16 text-center text-xs text-gray-500">
                {month.date.toLocaleDateString("en-US", {
                  month: "short",
                  year: "2-digit",
                })}
              </div>
            </div>
          ))}

          {/* Timeline line */}
          <div className="absolute top-20 h-1 w-full bg-gray-300"></div>

          {/* Render event clusters */}
          {eventClusters.map((cluster, clusterIndex) => (
            <div
              key={`cluster-${clusterIndex}`}
              className="absolute"
              style={{ left: `${cluster.position}%` }}
            >
              {/* If cluster has only one event, render normally */}
              {cluster.events.length === 1 ? (
                <div
                  className={`absolute flex h-40 w-8 -translate-x-4 flex-col items-center ${
                    cluster.events[0].type === "task" &&
                    cluster.events[0].data?.craftRecordID
                      ? "cursor-pointer"
                      : ""
                  }`}
                  style={{ top: "4px" }}
                  onClick={() => {
                    const event = cluster.events[0];
                    if (event.type === "task" && event.data?.craftRecordID) {
                      handleGoToWorkRecordAndTaskPage(event.data.craftRecordID);
                    }
                  }}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${getEventColorClass(cluster.events[0])}`}
                  >
                    {getEventIcon(cluster.events[0])}
                  </div>
                  <div className="mt-1 flex w-32 -translate-x-12 flex-col items-center">
                    <span className="max-w-full truncate text-xs font-semibold">
                      {cluster.events[0].label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(cluster.events[0].date)}
                    </span>
                  </div>
                </div>
              ) : (
                // If cluster has multiple events, stack them vertically
                <div className="absolute top-4 z-10 -translate-x-4">
                  {/* Multi-event indicator */}
                  <div className="relative mb-1 flex h-6 w-8 items-center justify-center">
                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
                      {cluster.events.length}
                    </span>
                  </div>

                  {/* Stacked events */}
                  <div className="flex flex-col items-center gap-2 rounded-lg border border-gray-300 bg-white p-2 shadow-sm">
                    {cluster.events.map((event, eventIndex) => (
                      <div
                        key={`event-${event.id}`}
                        className={`flex w-full cursor-pointer items-center gap-2 rounded p-1 hover:bg-gray-50 ${
                          event.type === "task" && event.data?.craftRecordID
                            ? "cursor-pointer"
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            event.type === "task" &&
                            event.data?.craftRecordID
                          ) {
                            handleGoToWorkRecordAndTaskPage(
                              event.data.craftRecordID,
                            );
                          }
                        }}
                      >
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${getEventColorClass(event)}`}
                        >
                          {getEventIcon(event)}
                        </div>
                        <div className="flex flex-col">
                          <span className="max-w-full truncate text-xs font-medium">
                            {event.label}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(event.date)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Start and end date labels */}
          <div className="absolute -bottom-6 left-[5%] text-xs font-medium text-gray-700">
            {formatDate(startDate)}
          </div>
          <div className="absolute -bottom-6 right-[5%] text-xs font-medium text-gray-700">
            {formatDate(endDate)}
          </div>
        </div>
      </div>
    </div>
  );
}
